import { useAppSelector } from '../../store/hooks';
import { Navigate } from 'react-router-dom';
import { ReactNode, useEffect, useState } from 'react';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isLoggedIn, user, tokens } = useAppSelector((state) => state.auth);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Check localStorage for user and tokens
    const storedUser = localStorage.getItem('user');
    const storedTokens = localStorage.getItem('tokens');
    
    console.log('🔍 ProtectedRoute Check:', {
      isLoggedIn,
      hasUser: !!user,
      hasTokens: !!tokens,
      hasStoredUser: !!storedUser,
      hasStoredTokens: !!storedTokens,
      userRole: user?.role,
      storedUserRole: storedUser ? JSON.parse(storedUser)?.role : null
    });

    // Allow some time for Redux to rehydrate from localStorage
    const timer = setTimeout(() => {
      setIsChecking(false);
    }, 500); // Increased timeout

    return () => clearTimeout(timer);
  }, [isLoggedIn, user, tokens]);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-light dark:bg-dark">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-slate-600 dark:text-slate-400">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Check if user has valid authentication
  const hasValidAuth = () => {
    // First check Redux state
    if (isLoggedIn && user && tokens) {
      return true;
    }
    
    // Fallback: check localStorage directly
    const storedUser = localStorage.getItem('user');
    const storedTokens = localStorage.getItem('tokens');
    
    if (storedUser && storedTokens) {
      try {
        const parsedUser = JSON.parse(storedUser);
        const parsedTokens = JSON.parse(storedTokens);
        return !!(parsedUser && parsedTokens && parsedTokens.accessToken);
      } catch (error) {
        console.warn('Error parsing auth data from localStorage:', error);
        // Clear invalid data
        localStorage.removeItem('user');
        localStorage.removeItem('tokens');
        return false;
      }
    }
    
    return false;
  };

  if (!hasValidAuth()) {
    console.log('❌ Authentication failed, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  console.log('✅ Authentication successful, allowing access');
  return <>{children}</>;
};

export default ProtectedRoute;
